/**
 * Hosts API (BYOH)
 * Routes to governance-service via /v1/governance/agent-hosts/*
 */

import { config } from '../utils/config'
import { apiClient } from './api-client'

export interface HostVPNConfig {
  assigned_ip: string
  gateway_internal_endpoint: string
  gateway_status: string
  peer_connected: boolean
}

export interface HostResponse {
  id: string
  name: string
  status: string
  organization_id: string
  vpn_config?: HostVPNConfig
  created_at: string
  updated_at: string
}

export interface HostListResponse {
  hosts: HostResponse[]
  total: number
}

/** File stored on a BYOH agent-host */
export interface HostFile {
  id: string
  filename: string
  size: number
  content_type: string
  purpose?: string
  tool_id?: string
  created_at: string
  expires_at?: string
}

/**
 * A file download the governance host-file proxy refused. status is the HTTP
 * status, which tells the file card why (agion/Agion#2003).
 */
export class HostFileDownloadError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'HostFileDownloadError'
    this.status = status
  }
}

/**
 * An agent host address refused before any request (agion/Agion#2003): the
 * host id or the file id is empty, "." or "..". An empty id leaves its path
 * segment out, and URL parsing reads "." and ".." as path steps even when they
 * are encoded ("%2e%2e" is ".."), so the request would reach another path. It
 * carries no HTTP status; the file card shows its retry text.
 */
export class HostPathIdError extends Error {
  readonly idKind: 'host' | 'file'

  constructor(idKind: 'host' | 'file', id: string) {
    super(`The ${idKind} id ${JSON.stringify(id)} cannot be a path segment of an agent host address.`)
    this.name = 'HostPathIdError'
    this.idKind = idKind
  }
}

/** One id as one path segment of an agent host address: encoded, and refused when it is a path step. */
function pathSegment(idKind: 'host' | 'file', id: string): string {
  if (id === '' || id === '.' || id === '..') throw new HostPathIdError(idKind, id)
  return encodeURIComponent(id)
}

/**
 * The governance path of one agent host. The host, its file upload and its
 * file downloads all start from it, so the host id is always one checked
 * path segment (agion/Agion#2003).
 */
function agentHostPath(hostId: string): string {
  return `/v1/governance/agent-hosts/${pathSegment('host', hostId)}`
}

export const hostsApi = {
  /**
   * List all hosts for the organization
   */
  listHosts: () => {
    return apiClient.get<HostListResponse>(`/v1/governance/agent-hosts`)
  },

  /**
   * The organisation's host groups, for naming a group-bound mission in the
   * sidebar (agion/Agion#2485). Never used to pick a host.
   */
  listHostGroups: async (): Promise<Array<{ id: string; name: string }>> => {
    const body = await apiClient.get<unknown>(`/v1/governance/host-groups`)
    const rows = Array.isArray(body) ? body : []
    return rows
      .filter((row): row is { id: string; name?: string } => typeof (row as { id?: unknown })?.id === 'string')
      .map((row) => ({ id: row.id, name: typeof row.name === 'string' && row.name ? row.name : row.id }))
  },

  /**
   * Get host details by ID. Async, so a refused host id (HostPathIdError) is a
   * rejected promise like any other failure.
   */
  getHost: async (hostId: string) => {
    return apiClient.get<HostResponse>(agentHostPath(hostId))
  },

  /**
   * Upload a file to a BYOH agent-host via governance proxy.
   * Files are stored on the host at /var/lib/agion/uploads/ and auto-expire after 7 days.
   *
   * Uses raw fetch (not apiClient) because:
   * 1. apiClient.uploadFile JSON-stringifies additionalData values, but form fields need plain strings
   * 2. Response uses snake_case (matches HostFile interface) — no camelCase conversion needed
   */
  uploadFileToHost: async (hostId: string, file: File, purpose: string = 'chat_attachment'): Promise<HostFile> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('purpose', purpose)

    const response = await fetch(
      `${config.apiUrl}${agentHostPath(hostId)}/files`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      },
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(
        (error as Record<string, string>).message ||
        (error as Record<string, string>).error ||
        'Failed to upload file to host',
      )
    }

    return response.json()
  },

  /**
   * Download a file from a BYOH agent-host via governance proxy.
   * Used for files produced by tool execution (output_files on
   * mission.tool.result). Same endpoint admin-ui's designer uses.
   *
   * Raw fetch (not apiClient): the response is binary, not JSON.
   * A refused download throws HostFileDownloadError with the HTTP status.
   * The file id comes from tool output; each id is one path segment
   * (agentHostPath, pathSegment), and an id that cannot be one throws
   * HostPathIdError.
   */
  downloadHostFile: async (hostId: string, fileId: string): Promise<Blob> => {
    const response = await fetch(
      `${config.apiUrl}${agentHostPath(hostId)}/files/${pathSegment('file', fileId)}/download`,
      {
        method: 'GET',
        credentials: 'include',
      },
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new HostFileDownloadError(
        (error as Record<string, string>).message ||
        (error as Record<string, string>).error ||
        'Failed to download file',
        response.status,
      )
    }

    return response.blob()
  },
}

// F7 (chat-ui catch-up): the Edge-host health probe was removed. The
// underlying endpoint never existed on the platform — governance-service's
// proxy only exposes the per-mission messages route. Connection status now
// surfaces via hostsApi.getHost(id).vpn_config.peer_connected (used by
// MissionChatPage).
